import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSupportRequest, fetchSupportRequests } from '../../store/Slices/supportSlice';
import {
  TextField,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import socket from '../../services/socket';
import { lightTheme, darkTheme } from '../../themes/themes';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import HomeIcon from '@mui/icons-material/Home';

const CreateSupportRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const { autoReply, loading, error } = useSelector((state) => state.support);
  const isLightTheme = useSelector((state) => state.theme.light);
  const theme = isLightTheme ? lightTheme : darkTheme;
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [initialMessage, setInitialMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?._id) {
      navigate('/login');
      return;
    }

    try {
      await dispatch(
        createSupportRequest({
          user_id: user._id,
          subject,
          description,
          initial_message: initialMessage,
        })
      ).unwrap();
      setSubject('');
      setDescription('');
      setInitialMessage('');
    } catch (err) {
      console.error('Failed to create support request:', err);
    }
  };

  return (
    <Card
      sx={{
        maxWidth: 500,
        mx: 'auto',
        mb: 4,
        bgcolor: theme.palette.background.paper,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': { transform: 'translateY(-4px)' },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
          Create Support Request
        </Typography>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress sx={{ color: theme.palette.brand.main }} />
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <TextField
            label="Subject"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            sx={{
              mb: 2,
              bgcolor: theme.palette.background.paper,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme.palette.divider },
                '&:hover fieldset': { borderColor: theme.palette.text.secondary },
              },
              '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
              '& .MuiInputBase-input': { color: theme.palette.text.primary },
            }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            sx={{
              mb: 2,
              bgcolor: theme.palette.background.paper,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme.palette.divider },
                '&:hover fieldset': { borderColor: theme.palette.text.secondary },
              },
              '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
              '& .MuiInputBase-input': { color: theme.palette.text.primary },
            }}
          />
          <TextField
            label="Initial Message"
            fullWidth
            multiline
            rows={2}
            value={initialMessage}
            onChange={(e) => setInitialMessage(e.target.value)}
            required
            sx={{
              mb: 2,
              bgcolor: theme.palette.background.paper,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: theme.palette.divider },
                '&:hover fieldset': { borderColor: theme.palette.text.secondary },
              },
              '& .MuiInputLabel-root': { color: theme.palette.text.secondary },
              '& .MuiInputBase-input': { color: theme.palette.text.primary },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: theme.palette.brand.main,
              color: '#fff',
              '&:hover': { bgcolor: '#d4a027' },
              borderRadius: '6px',
              px: 3,
              py: 1,
              fontWeight: 'bold',
            }}
          >
            Submit
          </Button>
        </form>
        {autoReply && (
          <Typography sx={{ color: theme.palette.success.main, mt: 2 }}>
            Auto Reply: {autoReply}
          </Typography>
        )}
        {error && (
          <Typography sx={{ color: theme.palette.error.main, mt: 2 }}>
            Error: {error}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const UserSupportRequests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const { requests } = useSelector((state) => state.support);
  const isLightTheme = useSelector((state) => state.theme.light);
  const theme = isLightTheme ? lightTheme : darkTheme;
  const [isSupportOnline, setIsSupportOnline] = useState(false);

  useEffect(() => {
    if (user?._id) {
      dispatch(fetchSupportRequests(user._id));
      socket.emit('join', { user_id: user._id });

      socket.on('support_status', (status) => {
        console.log('Support status received:', status);
        setIsSupportOnline(status.online);
      });

      return () => {
        socket.off('support_status');
      };
    } else {
      navigate('/login');
    }
  }, [dispatch, user, navigate]);

  const handleNavigateToChat = (requestId) => {
    navigate('/support/sessions', { state: { requestId } });
  };

  // تصفية الطلبات حسب الحالة
  const openRequests = requests.filter((request) => request.status === 'Open');
  const acceptedRequests = requests.filter((request) => request.status === 'Accepted');

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
       {/* حالة الدعم */}
       <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, mr: 1 }}>
          Support Status:
        </Typography>
        <FiberManualRecordIcon
          sx={{
            color: isSupportOnline ? '#4caf50' : '#f44336',
            fontSize: '1rem',
            animation: 'blink 1s infinite',
            mr: 1,
          }}
        />
        <Typography sx={{ color: isSupportOnline ? '#4caf50' : '#f44336' }}>
          {isSupportOnline ? 'Active' : 'Busy'}
        </Typography>
      </Box>
      {/* بطاقة الطلبات المفتوحة */}
      <Card
        sx={{
          mb: 4,
          bgcolor: theme.palette.background.paper,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
          overflow: 'hidden',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': { transform: 'translateY(-4px)' },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
            Open Requests
          </Typography>
          {openRequests.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 2 }}>
              No open support requests currently.
            </Typography>
          ) : (
            openRequests.map((request) => (
              <Box
                key={request._id}
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderRadius: '8px',
                  mb: 2,
                  p: 2,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                }}
              >
                <Typography sx={{ color: theme.palette.text.primary, fontWeight: 'medium' }}>
                  {request.subject}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary }}>
                  Description: {request.description}
                </Typography>
                <Typography sx={{ color: '#f44336', fontSize: '0.9rem' }}>
                  Status: {request.status}
                </Typography>
                <Button
                  variant="contained"
                  disabled
                  sx={{
                    mt: 1,
                    bgcolor: theme.palette.grey[500],
                    color: '#fff',
                    '&:hover': { bgcolor: theme.palette.grey[700] },
                    borderRadius: '6px',
                    px: 2,
                    py: 0.5,
                    fontSize: '0.8rem',
                  }}
                >
                  Awaiting Support
                </Button>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {/* بطاقة الطلبات المقبولة */}
      <Card
        sx={{
          mb: 4,
          bgcolor: theme.palette.background.paper,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
          overflow: 'hidden',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': { transform: 'translateY(-4px)' },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
            Accepted Requests
          </Typography>
          {acceptedRequests.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 2 }}>
              No accepted support requests currently.
            </Typography>
          ) : (
            acceptedRequests.map((request) => (
              <Box
                key={request._id}
                sx={{
                  bgcolor: theme.palette.background.paper,
                  borderRadius: '8px',
                  mb: 2,
                  p: 2,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                }}
              >
                <Typography sx={{ color: theme.palette.text.primary, fontWeight: 'medium' }}>
                  {request.subject}
                </Typography>
                <Typography sx={{ color: theme.palette.text.secondary }}>
                  Description: {request.description}
                </Typography>
                <Typography sx={{ color: '#4caf50', fontSize: '0.9rem' }}>
                  Status: {request.status}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => handleNavigateToChat(request._id)}
                  sx={{
                    mt: 1,
                    bgcolor: theme.palette.brand.main,
                    color: '#fff',
                    '&:hover': { bgcolor: '#d4a027' },
                    borderRadius: '6px',
                    px: 2,
                    py: 0.5,
                    fontSize: '0.8rem',
                  }}
                >
                  Go to Chat
                </Button>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

     
    </Box>
  );
};

const SupportRequestPage = () => {
  const isLightTheme = useSelector((state) => state.theme.light);
  const theme = isLightTheme ? lightTheme : darkTheme;
  const navigate = useNavigate();

  const handleHomeClick = () => {
    navigate('/home');
  };

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          bgcolor: theme.palette.background.paper,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleHomeClick}
              sx={{
                mr: 2,
                color: theme.palette.brand.main,
                '&:hover': { color: '#d4a027' },
              }}
            >
              <HomeIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 'bold',
              }}
            >
              Support Request Hub
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <style>
        {`
          @keyframes blink {
            0% { opacity: 1; }
            50% { opacity: 0.3; }
            100% { opacity: 1; }
          }
        `}
      </style>

      <Box sx={{ p: 3 }}>
        <CreateSupportRequest />
        <UserSupportRequests />
      </Box>
    </Box>
  );
};

export default SupportRequestPage;